import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin,
  Target,
  TrendingUp,
  Zap,
  AlertTriangle,
  ShieldCheck,
  BarChart3,
  Trophy,
  Rocket,
} from "lucide-react";

const INDUSTRIES = [
  "Restaurant",
  "Hospital",
  "Retail Store",
  "Office",
  "Hotel",
  "School",
  "Factory",
  "Cafe",
  "Salon",
  "Clinic",
  "Tuition Centre",
  "Fitness Centre",
  "Real Estate",
  "Supermarket",
  "Mini Mart",
];

const SG_AREAS = [
  "Orchard",
  "Bugis",
  "Tampines",
  "Jurong East",
  "Woodlands",
  "Punggol",
  "Yishun",
  "Bedok",
  "Hougang",
  "Clementi",
  "Toa Payoh",
  "Ang Mo Kio",
  "Sengkang",
  "Pasir Ris",
  "Serangoon",
  "Bishan",
  "Novena",
  "Tiong Bahru",
  "Holland Village",
  "Marina Bay",
];

const THREAT_COLORS = {
  high: "#FF007F",
  medium: "#FFB400",
  low: "#00F0FF",
};

/* -------------------------------------------------------
   HELPERS
------------------------------------------------------- */

function cleanText(value) {
  if (value === null || value === undefined) return "";

  if (typeof value === "string") {
    return value
      .replace(/\*\*/g, "")
      .replace(/^[-*•]\s*/, "")
      .replace(/^\d+[\.\)]\s*/, "")
      .trim();
  }

  return String(value);
}

function arrayFrom(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((item) => cleanText(item))
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(/\n+/)
      .map((item) => cleanText(item))
      .filter(Boolean);
  }

  return [cleanText(value)].filter(Boolean);
}

/*
  Converts Gemini's text response into readable sections.

  Example:

  MARKET OVERVIEW
  Singapore is...

  KEY COMPETITORS
  1. Company A
  2. Company B

  STRATEGY
  Improve...
*/
function formatAIAnalysis(analysis) {
  if (!analysis) return [];

  if (Array.isArray(analysis)) {
    return analysis.map((item, index) => ({
      title: `AI Insight ${index + 1}`,
      items: arrayFrom(item),
    }));
  }

  if (typeof analysis === "object") {
    return Object.entries(analysis).map(([key, value]) => ({
      title: key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase()),
      items:
        typeof value === "string"
          ? arrayFrom(value)
          : Array.isArray(value)
          ? arrayFrom(value)
          : [JSON.stringify(value, null, 2)],
    }));
  }

  const text = String(analysis)
    .replace(/\r/g, "")
    .trim();

  if (!text) return [];

  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const sections = [];
  let current = null;

  lines.forEach((line) => {
    const cleaned = line
      .replace(/^#{1,6}\s*/, "")
      .replace(/\*\*/g, "")
      .trim();

    /*
      Treat obvious headings as section headings.
    */
    const isHeading =
      /^([A-Z][A-Z\s&:/-]{3,})$/.test(cleaned) ||
      /^(\d+[\.\)]\s+[A-Z])/.test(cleaned) ||
      /^(market|competitor|competitive|strength|weakness|strategy|recommendation|opportunity|threat|quick win|action|summary|overview|pricing|positioning|advantage|next step)/i.test(
        cleaned
      );

    if (isHeading && !cleaned.match(/^[-*•]/)) {
      current = {
        title: cleaned.replace(/^\d+[\.\)]\s*/, ""),
        items: [],
      };

      sections.push(current);
      return;
    }

    if (!current) {
      current = {
        title: "Market Intelligence",
        items: [],
      };

      sections.push(current);
    }

    current.items.push(cleaned);
  });

  return sections.filter(
    (section) => section.title || section.items.length
  );
}

function displayValue(value) {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => cleanText(item)).join(" • ");
  }

  return JSON.stringify(value, null, 2);
}

/* -------------------------------------------------------
   COMPETITOR CARD
------------------------------------------------------- */

function CompetitorCard({ competitor, index }) {
  const threat =
    String(competitor?.threat_level || "medium").toLowerCase();

  const threatColor =
    THREAT_COLORS[threat] || THREAT_COLORS.medium;

  const name =
    competitor?.name ||
    competitor?.business_name ||
    `Competitor ${index + 1}`;

  const strengths = arrayFrom(competitor?.strengths);
  const weaknesses = arrayFrom(competitor?.weaknesses);

  return (
    <div
      className="rounded-3xl p-6 bg-white/[0.025] border border-white/10 hover:border-cyan-400/30 transition-all duration-300"
      data-testid={`competitor-card-${index}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-4">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-fuchsia-500/20 to-cyan-400/20 border border-white/10 flex items-center justify-center text-xl">
            {index === 0
              ? "🥇"
              : index === 1
              ? "🥈"
              : index === 2
              ? "🥉"
              : "🏢"}
          </div>

          <div>
            <h4 className="text-lg font-bold text-white">
              {name}
            </h4>

            {competitor?.location && (
              <div className="mt-1 flex items-center gap-1 text-xs text-white/50">
                <MapPin size={11} />
                {competitor.location}
              </div>
            )}

            {competitor?.distance_km !== undefined && (
              <div className="mt-1 text-xs text-white/40">
                {Number(competitor.distance_km).toFixed(1)} km away
              </div>
            )}
          </div>
        </div>

        <span
          className="shrink-0 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest"
          style={{
            background: `${threatColor}18`,
            color: threatColor,
            border: `1px solid ${threatColor}35`,
          }}
        >
          {threat} threat
        </span>
      </div>

      {/* Strengths / Weaknesses */}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-emerald-400/[0.04] border border-emerald-300/10 p-4">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-emerald-300">
            <ShieldCheck size={14} />
            Strengths
          </div>

          <ul className="mt-3 space-y-2">
            {(strengths.length ? strengths : ["No strengths provided"]).map(
              (item, i) => (
                <li
                  key={i}
                  className="text-sm leading-6 text-white/70 flex gap-2"
                >
                  <span className="text-emerald-300">✓</span>
                  <span>{item}</span>
                </li>
              )
            )}
          </ul>
        </div>

        <div className="rounded-2xl bg-orange-400/[0.04] border border-orange-300/10 p-4">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-orange-300">
            <AlertTriangle size={14} />
            Weaknesses
          </div>

          <ul className="mt-3 space-y-2">
            {(weaknesses.length
              ? weaknesses
              : ["No weaknesses provided"]
            ).map((item, i) => (
              <li
                key={i}
                className="text-sm leading-6 text-white/70 flex gap-2"
              >
                <span className="text-orange-300">⚠</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------
   MAIN COMPONENT
------------------------------------------------------- */

export default function CompetitorAnalysis() {
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("Restaurant");
  const [city, setCity] = useState("Singapore");
  const [area, setArea] = useState("Orchard");

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  /* -------------------------------------------------------
     API CALL
  ------------------------------------------------------- */

  const run = async () => {
    if (!industry || !city) {
      toast.error("Please select industry and city");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/competitor-analysis`,
        {
          business_name: businessName || null,
          industry,
          city,
          area: area || null,
        },
        {
          timeout: 60000,
          headers: {
            "Content-Type": "application/json",
            apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
            Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
        }
      );

      console.log(
        "Competitor Analysis API Response:",
        response.data
      );

      setResult(response.data);

      toast.success("Market intelligence ready");
    } catch (error) {
      console.error("Competitor Analysis Error:", error);

      toast.error(
        error?.response?.data?.error ||
          "Analysis failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------
     DATA
  ------------------------------------------------------- */

  const competitors = Array.isArray(result?.competitors)
    ? result.competitors
    : [];

  const analysisSections = result?.analysis
    ? formatAIAnalysis(result.analysis)
    : [];

  const strategy =
    result?.strategy ||
    result?.munnexa_strategy ||
    result?.recommendations;

  const quickWins =
    result?.quick_wins ||
    result?.quickWins ||
    result?.actions;

  /* -------------------------------------------------------
     UI
  ------------------------------------------------------- */

  return (
    <section
      id="competitors"
      className="relative py-28 px-6"
      data-testid="competitor-section"
    >
      <div className="max-w-7xl mx-auto">

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="text-center mb-12">
          <p className="font-mono text-[11px] tracking-[0.4em] text-cyan-300/80 mb-3">
            ◆ COMPETITOR ANALYSIS · LIVE
          </p>

          <h2 className="text-4xl sm:text-5xl font-[Outfit] font-bold text-white">
            Scan your market.{" "}
            <span className="text-gradient">
              Outplay every rival.
            </span>
          </h2>

          <p className="mt-4 text-white/60 max-w-2xl mx-auto">
            Pick your business and location. NEXA AI maps your
            competitive landscape and engineers a custom strategy
            for your business.
          </p>
        </div>

        {/* =================================================
            INPUT FORM
        ================================================= */}

        <div
          className="glass-strong holo-border rounded-3xl p-6 sm:p-8 grid lg:grid-cols-5 gap-4 items-end"
          data-testid="competitor-form"
        >

          {/* Business */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-white/55">
              Business Name
            </label>

            <input
              value={businessName}
              onChange={(e) =>
                setBusinessName(e.target.value)
              }
              placeholder="e.g. Apex Bistro"
              data-testid="compet-name"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-cyan-400/60"
            />
          </div>

          {/* Industry */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-white/55">
              Industry
            </label>

            <select
              value={industry}
              onChange={(e) =>
                setIndustry(e.target.value)
              }
              data-testid="compet-industry"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-cyan-400/60"
            >
              {INDUSTRIES.map((item) => (
                <option
                  key={item}
                  value={item}
                  className="bg-[#0A0F24]"
                >
                  {item}
                </option>
              ))}
            </select>
          </div>

          {/* City */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-white/55">
              City / Country
            </label>

            <input
              value={city}
              onChange={(e) =>
                setCity(e.target.value)
              }
              data-testid="compet-city"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-cyan-400/60"
            />
          </div>

          {/* Area */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-white/55">
              Area / District
            </label>

            <input
              list="sg-areas"
              value={area}
              onChange={(e) =>
                setArea(e.target.value)
              }
              data-testid="compet-area"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-cyan-400/60"
            />

            <datalist id="sg-areas">
              {SG_AREAS.map((item) => (
                <option key={item} value={item} />
              ))}
            </datalist>
          </div>

          {/* Button */}
          <button
            onClick={run}
            disabled={loading}
            data-testid="compet-analyze-btn"
            className="btn-gradient rounded-full py-3.5 px-6 text-sm font-semibold disabled:opacity-50"
          >
            {loading
              ? "Scanning market..."
              : "Scan Market →"}
          </button>
        </div>

        {/* =================================================
            LOADING
        ================================================= */}

        {loading && (
          <div
            className="mt-8 grid lg:grid-cols-2 gap-5"
            data-testid="competitor-loading"
          >
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="glass rounded-3xl p-6 h-48 shimmer"
              />
            ))}
          </div>
        )}

        {/* =================================================
            RESULTS
        ================================================= */}

        {result && !loading && (
          <div
            className="mt-10 space-y-7"
            data-testid="competitor-result"
          >

            {/* =================================================
                REPORT HEADER
            ================================================= */}

            <div className="glass rounded-3xl p-7 border border-cyan-400/20">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">

                <div>
                  <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300">
                    <BarChart3 size={14} />
                    NEXA AI · COMPETITIVE INTELLIGENCE
                  </div>

                  <h3 className="mt-3 text-3xl font-bold text-white">
                    Market Intelligence Report
                  </h3>

                  <p className="mt-2 text-sm text-white/50">
                    {businessName ||
                      `${industry} business`}{" "}
                    · {city}
                    {area ? ` · ${area}` : ""}
                  </p>
                </div>

                <div className="rounded-2xl px-5 py-4 bg-cyan-400/10 border border-cyan-300/20">
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300/60">
                    Analysis Status
                  </div>

                  <div className="mt-1 text-sm font-semibold text-cyan-200">
                    ✓ Complete
                  </div>
                </div>

              </div>
            </div>

            {/* =================================================
                MARKET SUMMARY
            ================================================= */}

            {result.market_summary ||
            result.summary ||
            result.overview ? (
              <div className="glass rounded-3xl p-7 border border-cyan-300/10">

                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-300/20 flex items-center justify-center">
                    📊
                  </div>

                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/70">
                      MARKET OVERVIEW
                    </div>

                    <h3 className="text-2xl font-bold text-white">
                      Market Summary
                    </h3>
                  </div>
                </div>

                <div className="mt-5 rounded-2xl bg-white/[0.025] border border-white/[0.06] p-5">
                  <p className="text-sm md:text-[15px] leading-7 text-white/75 whitespace-pre-wrap">
                    {displayValue(
                      result.market_summary ||
                        result.summary ||
                        result.overview
                    )}
                  </p>
                </div>
              </div>
            ) : null}

            {/* =================================================
                AI ANALYSIS
            ================================================= */}

            {analysisSections.length > 0 && (
              <div className="space-y-5">

                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-purple-500/10 border border-purple-300/20 flex items-center justify-center">
                    🧠
                  </div>

                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-purple-300/70">
                      ARTIFICIAL INTELLIGENCE
                    </div>

                    <h3 className="text-2xl font-bold text-white">
                      NEXA AI Analysis
                    </h3>
                  </div>
                </div>

                {analysisSections.map(
                  (section, sectionIndex) => (
                    <div
                      key={sectionIndex}
                      className="glass rounded-3xl p-6 border border-white/10 hover:border-cyan-400/20 transition-all"
                    >

                      <div className="flex items-start gap-4">

                        <div className="w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-cyan-400/20 to-purple-500/20 border border-cyan-300/20 flex items-center justify-center text-lg">
                          {sectionIndex === 0
                            ? "📊"
                            : sectionIndex === 1
                            ? "🏆"
                            : sectionIndex === 2
                            ? "⚔️"
                            : sectionIndex === 3
                            ? "💪"
                            : sectionIndex === 4
                            ? "⚠️"
                            : sectionIndex === 5
                            ? "🚀"
                            : "🎯"}
                        </div>

                        <div>
                          <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/60">
                            NEXA INSIGHT
                          </div>

                          <h4 className="mt-1 text-xl font-bold text-white">
                            {section.title}
                          </h4>
                        </div>
                      </div>

                      <div className="mt-5 space-y-3">
                        {section.items.map(
                          (item, itemIndex) => (
                            <div
                              key={itemIndex}
                              className="flex gap-3 rounded-2xl bg-white/[0.025] border border-white/[0.06] p-4"
                            >
                              <div className="mt-2 w-2 h-2 shrink-0 rounded-full bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.8)]" />

                              <p className="text-sm md:text-[15px] leading-7 text-white/75">
                                {cleanText(item)}
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )
                )}
              </div>
            )}

            {/* =================================================
                COMPETITORS
            ================================================= */}

            {competitors.length > 0 && (
              <div className="glass rounded-3xl p-7">

                <div className="flex items-center gap-4 mb-7">

                  <div className="w-12 h-12 rounded-2xl bg-pink-500/10 border border-pink-400/20 flex items-center justify-center">
                    <Trophy size={22} className="text-pink-300" />
                  </div>

                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-pink-300/70">
                      COMPETITIVE LANDSCAPE
                    </div>

                    <h3 className="text-2xl font-bold text-white">
                      Local Competitors
                    </h3>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {competitors.map(
                    (competitor, index) => (
                      <CompetitorCard
                        key={index}
                        competitor={competitor}
                        index={index}
                      />
                    )
                  )}
                </div>
              </div>
            )}

            {/* =================================================
                STRATEGY
            ================================================= */}

            {strategy && (
              <div className="rounded-3xl p-7 bg-gradient-to-br from-fuchsia-500/10 via-purple-500/10 to-cyan-500/10 border border-fuchsia-400/20">

                <div className="flex items-center gap-4">

                  <div className="w-12 h-12 rounded-2xl bg-fuchsia-500/10 border border-fuchsia-300/20 flex items-center justify-center">
                    <Target size={23} className="text-fuchsia-300" />
                  </div>

                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fuchsia-300">
                      MUNNEXA ADVANTAGE
                    </div>

                    <h3 className="text-2xl font-bold text-white">
                      Recommended Strategy
                    </h3>
                  </div>
                </div>

                <div className="mt-6 rounded-2xl bg-black/20 border border-white/5 p-6">
                  <p className="whitespace-pre-wrap text-sm md:text-[15px] leading-7 text-white/80">
                    {displayValue(strategy)}
                  </p>
                </div>
              </div>
            )}

            {/* =================================================
                QUICK WINS
            ================================================= */}

            {quickWins && (
              <div className="glass rounded-3xl p-7 border border-yellow-300/10">

                <div className="flex items-center gap-4">

                  <div className="w-12 h-12 rounded-2xl bg-yellow-400/10 border border-yellow-300/20 flex items-center justify-center">
                    <Zap size={23} className="text-yellow-300" />
                  </div>

                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-yellow-300/70">
                      ACTION PLAN
                    </div>

                    <h3 className="text-2xl font-bold text-white">
                      Quick Wins
                    </h3>
                  </div>
                </div>

                <div className="mt-6 grid md:grid-cols-2 gap-4">
                  {arrayFrom(quickWins).map(
                    (item, index) => (
                      <div
                        key={index}
                        className="rounded-2xl bg-yellow-400/[0.03] border border-yellow-300/10 p-5"
                      >
                        <div className="flex gap-3">
                          <div className="text-xl">
                            ⚡
                          </div>

                          <p className="text-sm leading-6 text-white/75">
                            {item}
                          </p>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {/* =================================================
                OPPORTUNITIES
            ================================================= */}

            {(result.opportunities ||
              result.opportunity ||
              result.growth_opportunities) && (
              <div className="glass rounded-3xl p-7 border border-emerald-300/10">

                <div className="flex items-center gap-4">

                  <div className="w-12 h-12 rounded-2xl bg-emerald-400/10 border border-emerald-300/20 flex items-center justify-center">
                    <TrendingUp
                      size={23}
                      className="text-emerald-300"
                    />
                  </div>

                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-300/70">
                      GROWTH POTENTIAL
                    </div>

                    <h3 className="text-2xl font-bold text-white">
                      Market Opportunities
                    </h3>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  {arrayFrom(
                    result.opportunities ||
                      result.opportunity ||
                      result.growth_opportunities
                  ).map((item, index) => (
                    <div
                      key={index}
                      className="rounded-2xl bg-emerald-400/[0.03] border border-emerald-300/10 p-4 flex gap-3"
                    >
                      <span className="text-emerald-300">
                        ◆
                      </span>

                      <p className="text-sm leading-6 text-white/75">
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* =================================================
                FINAL CTA
            ================================================= */}

            <div className="rounded-3xl p-7 bg-gradient-to-r from-fuchsia-500/10 via-purple-500/10 to-cyan-500/10 border border-cyan-300/10">

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">

                <div className="flex items-center gap-4">

                  <div className="w-12 h-12 rounded-2xl bg-cyan-400/10 border border-cyan-300/20 flex items-center justify-center">
                    <Rocket
                      size={23}
                      className="text-cyan-300"
                    />
                  </div>

                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/70">
                      NEXT MOVE
                    </div>

                    <h3 className="text-xl font-bold text-white">
                      Turn intelligence into action.
                    </h3>
                  </div>

                </div>

                <button
                  onClick={() =>
                    toast.success(
                      "MUNNEXA strategy activation initiated"
                    )
                  }
                  className="btn-gradient rounded-full py-3 px-6 text-sm font-semibold"
                >
                  Activate Strategy →
                </button>

              </div>
            </div>

            {/* =================================================
                DEBUG / FALLBACK
            ================================================= */}

            {!analysisSections.length &&
              !competitors.length &&
              !strategy &&
              !quickWins &&
              !result.market_summary &&
              !result.summary &&
              !result.overview && (
                <div className="glass rounded-3xl p-7 border border-yellow-300/10">

                  <div className="flex items-center gap-3">
                    <AlertTriangle className="text-yellow-300" />

                    <div>
                      <h3 className="text-xl font-bold text-white">
                        Analysis received
                      </h3>

                      <p className="mt-1 text-sm text-white/50">
                        The API returned data, but its structure
                        does not match the display fields yet.
                      </p>
                    </div>
                  </div>

                  <pre className="mt-5 rounded-2xl bg-black/30 p-5 overflow-auto text-xs text-cyan-200/80">
                    {JSON.stringify(result, null, 2)}
                  </pre>

                </div>
              )}

          </div>
        )}
      </div>
    </section>
  );
}
